/**
 * A PDF, drawn by PDF.js the same way in every browser (iOS Safari's own viewer shows only a first page in a frame):
 * its pages one under the other, each drawn when it scrolls into view, sharp on the screen's density, with its text
 * over it for selecting and finding. Fitted to the width, with zoom buttons; a pinch (or ⌘/Ctrl and the wheel, or a
 * Mac trackpad's pinch) zooms about the fingers. The chrome follows the theme; the pages stay as printed.
 *
 * Bare (a report in a conversation): no toolbar, no zoom, its first pages fitted to the width.
 *
 * Each page has room for layers over it (`overlay`): comments pinned to a spot of a page can come as one.
 */

import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { t } from "../../i18n/index.ts";
import { showDark } from "../../lib/documents.ts";
import {
  clampZoom,
  fitWidth,
  GUTTER,
  pageAt,
  pageSize,
  type Scroll,
  SETTLE_MS,
  stepZoom,
  zoomScroll,
} from "../../lib/pdfView.ts";
import type { Point } from "../../lib/pinch.ts";
import { DarkToggle } from "../DarkToggle.tsx";
import { useHandZoom } from "../handZoom.ts";
import { Spinner } from "../Spinner.tsx";
import { LINK } from "../styles.ts";
import { ViewerBar } from "../ViewerBar.tsx";
import { pdfEngine } from "./load.ts";
import { PdfPresence } from "./PdfPresence.tsx";
import type { Engine, OpenError, PdfDoc, PdfLink, PdfPage } from "./types.ts";

/** A page as drawn, for what's laid over it. */
interface PageView {
  /** 1-based. */
  n: number;
  width: number;
  height: number;
  zoom: number;
}

export interface PdfViewerProps {
  url: string;
  title: string;
  /** No toolbar and no zoom: its first `limit` pages fitted to the width. */
  bare?: boolean;
  /** At most this many pages (bare: one). */
  limit?: number;
  /** As tall as its box (a quick look's); otherwise up to 48rem, scrolling inside. Not when bare. */
  fill?: boolean;
  /**
   * The toolbar has a link to the original (where nothing beside it has one): this PDF (true), or the file it was made
   * from (its link: an Office document's, whose PDF this is), which a PDF that can't be shown links to as well.
   */
  original?: boolean | string;
  /** How many pages it has, once that's known. */
  onPages?: (n: number) => void;
  /** Laid over each page (pointer events off unless its own elements turn them on). */
  overlay?: (page: PageView) => ComponentChildren;
  /** More in its bar, after its own (a converted document's Edit). */
  controls?: ComponentChildren;
  /** Its presence room (an item's own PDF: `item:<id>`) and how you're there (`page`, `peek`): pointers shared. */
  presence?: { room: string; view: string } | undefined;
}

type State =
  | { status: "loading" }
  | { status: "error"; reason: OpenError }
  | { status: "ready"; doc: PdfDoc; first: { width: number; height: number } };

/** Opens `url` with the engine; the document is let go when the viewer goes or the link changes. */
function useDocument(url: string, load: () => Promise<Engine>, onPages?: (n: number) => void): State {
  const [state, setState] = useState<State>({ status: "loading" });
  const told = useRef(onPages);
  told.current = onPages;
  useEffect(() => {
    let alive = true;
    let doc: PdfDoc | null = null;
    let cancel: (() => void) | null = null;
    setState({ status: "loading" });
    load()
      .then(async (engine) => {
        if (!alive) {
          return;
        }
        const task = engine.open(url);
        cancel = task.cancel;
        try {
          doc = await task.promise;
          const first = await doc.page(1);
          if (alive) {
            setState({ status: "ready", doc, first: { width: first.width, height: first.height } });
            told.current?.(doc.pages);
          }
        } catch (e: unknown) {
          if (alive) {
            setState({ status: "error", reason: engine.reason(e) });
          }
        }
      })
      .catch(() => {
        // The chunk didn't load (offline, or replaced by a deploy).
        if (alive) {
          setState({ status: "error", reason: "failed" });
        }
      });
    return () => {
      alive = false;
      // An open document goes (its download with it); one still downloading stops.
      if (doc) {
        doc.destroy();
      } else {
        cancel?.();
      }
    };
  }, [url, load]);
  return state;
}

/** The width of `ref`'s box (CSS pixels), kept as it changes. */
function useWidth(ref: { current: HTMLElement | null }): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current as HTMLElement;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

/** `value`, once it has stayed the same for `ms`. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    if (Object.is(value, settled)) {
      return;
    }
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, settled, ms]);
  return settled;
}

/** Whether `ref` is in view of `root` (or the window), or near it; true where nobody can tell. */
function useNear(ref: { current: HTMLElement | null }, root: { current: HTMLElement | null } | null): boolean {
  const [near, setNear] = useState(typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      return;
    }
    // A screenful above and below: pages are drawn before they come into view.
    const io = new IntersectionObserver((entries) => setNear(entries.some((e) => e.isIntersecting)), {
      root: root?.current ?? null,
      rootMargin: "100% 0px",
    });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, root]);
  return near;
}

interface PageProps {
  doc: PdfDoc;
  n: number;
  /** Its size until it's known (the first page's). */
  guess: { width: number; height: number };
  zoom: number;
  /** The zoom it's drawn at (the zoom, once it stops changing). */
  drawn: number;
  text: boolean;
  root: { current: HTMLElement | null } | null;
  overlay: PdfViewerProps["overlay"];
  label: string;
}

/** One page: its room (sized before it's drawn), the canvas, its text, what's laid over it. */
function Page({ doc, n, guess, zoom, drawn, text, root, overlay, label }: PageProps) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const near = useNear(box, root);
  const [page, setPage] = useState<PdfPage | null>(null);
  const [failed, setFailed] = useState(false);
  const [links, setLinks] = useState<readonly PdfLink[]>([]);
  // Its links (with its text: not on a bare page), read once it's loaded.
  useEffect(() => {
    if (!(page && text)) {
      return;
    }
    let alive = true;
    page.links().then(
      (l) => alive && setLinks(l),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [page, text]);
  useEffect(() => {
    if (!near || page) {
      return;
    }
    let alive = true;
    doc.page(n).then(
      (p) => alive && setPage(p),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [near, page, doc, n]);
  useEffect(() => {
    const c = canvas.current;
    if (!(page && c)) {
      return;
    }
    if (!near) {
      // Far out of view: its pixels go (a long PDF would otherwise hold every page's in memory).
      c.width = 0;
      c.height = 0;
      return;
    }
    const size = pageSize(page, drawn, window.devicePixelRatio || 1);
    c.width = size.pixelWidth;
    c.height = size.pixelHeight;
    const tasks = [page.render(c, drawn, size.outputScale)];
    const l = layer.current;
    if (text && l) {
      l.replaceChildren();
      l.style.setProperty("--total-scale-factor", String(drawn));
      tasks.push(page.text(l, drawn));
    }
    for (const task of tasks) {
      // A drawing stopped because the page moved on isn't a failure.
      task.promise.catch(() => undefined);
    }
    return () => {
      for (const task of tasks) {
        task.cancel();
      }
    };
  }, [page, near, drawn, text]);
  const size = page ?? guess;
  const width = Math.floor(size.width * zoom);
  const height = Math.floor(size.height * zoom);
  return (
    <div
      ref={box}
      role="img"
      aria-label={label}
      data-page={n}
      class="relative shrink-0 shadow-md ring-1 ring-zinc-950/10"
      // The page as printed, whatever the theme: white under the canvas until it's drawn.
      style={{ width: `${width}px`, height: `${height}px`, background: "#fff" }}
    >
      <canvas ref={canvas} class="absolute inset-0 size-full" />
      {failed ? (
        <span class="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-zinc-500">
          {t("pdf.pageFailed")}
        </span>
      ) : null}
      {text ? (
        <div
          ref={layer}
          class="textLayer"
          // Stretched pages (a zoom not drawn yet) hide their text: it would sit beside the words.
          style={{
            visibility: zoom === drawn ? "visible" : "hidden",
            "--scale-round-x": "1px",
            "--scale-round-y": "1px",
          }}
        />
      ) : null}
      {links.map((l) => (
        <a
          key={`${l.url}@${l.left},${l.top}`}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          title={l.url}
          data-link=""
          class="absolute rounded-sm hover:bg-blue-500/10 hover:ring-1 hover:ring-blue-500/40"
          style={{
            left: `${l.left * zoom}px`,
            top: `${l.top * zoom}px`,
            width: `${l.width * zoom}px`,
            height: `${l.height * zoom}px`,
          }}
        >
          <span class="sr-only">{l.url}</span>
        </a>
      ))}
      {overlay ? <div class="pointer-events-none absolute inset-0">{overlay({ n, width, height, zoom })}</div> : null}
    </div>
  );
}

/** Why it isn't shown, and the original to open instead. */
function Failed({ reason, url }: { reason: OpenError; url: string }) {
  return (
    <div class="flex flex-col items-center justify-center gap-2 p-6 text-center text-sm text-zinc-600">
      <p>{t(`pdf.${reason}`)}</p>
      <a href={url} target="_blank" rel="noopener noreferrer" class={LINK}>
        {t("item.download")}
      </a>
    </div>
  );
}

export function PdfViewer({
  url,
  title,
  bare = false,
  limit,
  fill = false,
  original = false,
  onPages,
  overlay,
  controls,
  presence,
}: PdfViewerProps) {
  const state = useDocument(url, pdfEngine.load, onPages);
  const scroller = useRef<HTMLDivElement>(null);
  const width = useWidth(scroller);
  // "fit": as wide as the box; else a zoom chosen by hand.
  const [chosen, setChosen] = useState<number | "fit">("fit");
  const first = state.status === "ready" ? state.first : null;
  const fitted = first ? fitWidth(first.width, width, bare ? 0 : GUTTER) : 1;
  const zoom = chosen === "fit" || bare ? fitted : chosen;
  const drawn = useSettled(zoom, SETTLE_MS);
  const [current, setCurrent] = useState(1);
  // Where to scroll once the new zoom is laid out (so what was under the pointer stays there).
  const pending = useRef<Scroll[]>([]).current;
  const zoomTo = (z: number, scroll: (ratio: number) => Scroll) => {
    const next = clampZoom(z);
    if (next !== zoom) {
      pending.splice(0, pending.length, scroll(next / zoom));
      setChosen(next);
    }
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: `zoom` is the trigger: scroll once it's laid out.
  useLayoutEffect(() => {
    const el = scroller.current as HTMLDivElement;
    const to = pending.pop();
    if (to) {
      el.scrollLeft = to.left;
      el.scrollTop = to.top;
    }
  }, [zoom]);
  useHandZoom(scroller, zoom, zoomTo, !bare && state.status === "ready");
  const count = state.status === "ready" ? Math.min(state.doc.pages, limit ?? (bare ? 1 : state.doc.pages)) : 0;
  const pages = useMemo(() => Array.from({ length: count }, (_, i) => i + 1), [count]);
  const onScroll = () => {
    const el = scroller.current as HTMLDivElement;
    const tops = [...el.querySelectorAll<HTMLElement>("[data-page]")].map((p) => p.offsetTop);
    setCurrent(pageAt(tops, el.scrollTop, el.clientHeight));
  };
  const center = (): Point => {
    const el = scroller.current;
    return { x: (el?.clientWidth ?? 0) / 2, y: (el?.clientHeight ?? 0) / 2 };
  };
  const scrolled = (): Scroll => ({ left: scroller.current?.scrollLeft ?? 0, top: scroller.current?.scrollTop ?? 0 });
  const height = fill ? "min-h-0 flex-1" : "h-[48rem] max-h-[80vh]";
  const source = typeof original === "string" ? original : null;
  const body = (() => {
    if (state.status === "error") {
      return <Failed reason={state.reason} url={source ?? url} />;
    }
    if (state.status === "loading" || !first) {
      return (
        <div class={`flex items-center justify-center gap-2 text-sm text-zinc-500 ${bare ? "h-40" : "h-full"}`}>
          <Spinner small={true} />
          {t("pdf.loading")}
        </div>
      );
    }
    return (
      <div
        class={`flex w-max min-w-full flex-col items-center ${bare ? "" : "gap-3"}`}
        style={bare ? undefined : { padding: `${GUTTER}px` }}
      >
        {pages.map((n) => (
          <Page
            key={n}
            doc={state.doc}
            n={n}
            guess={first}
            zoom={zoom}
            drawn={drawn}
            text={!bare}
            root={bare ? null : scroller}
            overlay={overlay}
            label={t("pdf.page", { page: n })}
          />
        ))}
      </div>
    );
  })();
  if (bare) {
    return (
      <figure aria-label={title} data-pdf="bare" class="w-full">
        <div ref={scroller} class="w-full overflow-hidden">
          {body}
        </div>
      </figure>
    );
  }
  return (
    <section
      aria-label={title}
      data-pdf="full"
      // Dark colours: PDF.js has none of its own, so the pages are inverted with their hues turned back (a picture
      // in it inverts too), not quite all the way: white paper comes out #1b1b1b and black ink #e4e4e4, as a
      // document's dark colours have them (office/paint.ts).
      class={`flex w-full flex-col overflow-hidden rounded-md border border-zinc-200 bg-zinc-100 ${height} ${showDark() ? "[&_canvas]:invert-[0.894] [&_canvas]:hue-rotate-180" : ""}`}
      data-frame={fill ? "" : undefined}
    >
      {state.status === "ready" ? (
        <ViewerBar
          page={current}
          pages={state.doc.pages}
          zoom={zoom}
          fitted={chosen === "fit"}
          onZoom={(dir) => zoomTo(stepZoom(zoom, dir), (r) => zoomScroll(scrolled(), center(), r))}
          onFit={() => {
            pending.length = 0;
            setChosen("fit");
          }}
          original={source ?? (original ? url : null)}
        >
          <DarkToggle />
          {controls}
        </ViewerBar>
      ) : null}
      <div
        ref={scroller}
        onScroll={onScroll}
        // One finger scrolls (the browser's own); two zoom the pages, not the whole page (see useHandZoom).
        class="min-h-0 flex-1 touch-pan-x touch-pan-y overflow-auto overscroll-contain"
      >
        {body}
      </div>
      {presence && state.status === "ready" ? (
        <PdfPresence room={presence.room} view={presence.view} scroller={scroller} zoom={zoom} />
      ) : null}
    </section>
  );
}
